import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createAssetsClient } from './api';
import type { A2vAsset, AssetsFilter, AssetUploadPayload } from './model';

export const assetsQueryKey = ['a2v', 'assets'] as const;
export const assetsListsQueryKey = [...assetsQueryKey, 'list'] as const;
export const assetTagsQueryKey = [...assetsQueryKey, 'tags'] as const;

function useAssetsClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return createAssetsClient({
    config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
    accessToken: session.accessToken,
  });
}

export function useAssetsQuery(filter: AssetsFilter) {
  const client = useAssetsClient();
  return useQuery({
    queryKey: [...assetsListsQueryKey, filter],
    queryFn: () => client.list(filter),
  });
}

export function useAssetTagsQuery(searchTerm: string) {
  const client = useAssetsClient();
  return useQuery({
    queryKey: [...assetTagsQueryKey, searchTerm],
    queryFn: () => client.listTags(searchTerm),
    enabled: searchTerm.trim().length > 0,
  });
}

export function useAssetMutations() {
  const client = useAssetsClient();
  const queryClient = useQueryClient();
  const invalidateChangedAssets = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: assetsListsQueryKey }),
      queryClient.invalidateQueries({ queryKey: assetTagsQueryKey, refetchType: 'none' }),
    ]);
  };

  return {
    upload: useMutation({
      mutationFn: (payload: AssetUploadPayload) => client.upload(payload),
      onSuccess: invalidateChangedAssets,
    }),
    update: useMutation({
      mutationFn: ({ asset, tags }: { asset: A2vAsset; tags: string[] }) =>
        client.update(asset, tags),
      onSuccess: invalidateChangedAssets,
    }),
  };
}
